import type { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import * as React from 'react';
import Emoji from 'react-emoji-render';
import { rgba } from 'polished';
import styled from 'styled-components';

import { useModalConext } from '@/components/pricing/ModalProvider';

import { type Stylez } from '../components/design-system/Button';
import { LinkWithQuery } from '../components/global/LinkWithQuery';
import {
  DOCS_LINKS,
  INTERNAL_LINKS,
  SIGNUP_LINK,
  TRACKING_LABELS
} from '../utils/index';

const ComputeModalToggle = styled.button`
  text-decoration: underline;
  padding: 0;
  border: 0;
  color: currentColor;
  font-size: inherit;
  font-family: ${({ theme }) => theme.fontFamily.SANS}, sans-serif;
  background-color: transparent;
  transition: background-color
    ${({ theme }) => `${theme.animation.DURATION} ${theme.animation.EASING}`};
  cursor: pointer;

  &:hover {
    text-decoration: none;
    background-color: ${({ theme }) => rgba(theme.highlightColor, 0.3)};
  }

  &:active {
    background-color: ${({ theme }) => rgba(theme.highlightColor, 0.7)};
  }
`;

function ComputeModalLink(
  props: PropsWithChildren<
    Pick<ComponentProps<typeof ComputeModalToggle>, 'style'>
  >
) {
  const openComputeModal = useModalConext();
  return <ComputeModalToggle {...props} onClick={openComputeModal} />;
}

const SoonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;

  &:hover {
    cursor: wait;
  }
`;

const SoonBadge = styled.span`
  display: flex;
  gap: 3px;
  align-items: center;
  border: 1px solid ${({ theme }) => rgba(theme.highlightColor, 0.5)};
  color: ${({ theme }) => rgba(theme.colors.WHITE, 0.8)};
  padding: 1px 3px;
  font-weight: ${({ theme }) => theme.fontWeight.MEDIUM};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  max-height: 20px;
  white-space: nowrap;
  margin-right: 10px;

  && {
    opacity: 1;
  }
`;

interface ComingSoonFeatureProps {
  label: string;
}

export const ComingSoonFeature: React.ComponentType<ComingSoonFeatureProps> =
  React.memo(({ label }) => {
    return (
      <SoonWrapper>
        {label}
        <SoonBadge>
          Soon <Emoji text=":eyes:" />
        </SoonBadge>
      </SoonWrapper>
    );
  });

const communitySummaryFeatures = [
  'Unlimited queries',
  'Up to 5 projects',
  'Up to 100 Magic actions per Editor/month',
  'Embed visualizations or code cells anywhere',
  'Try the latest techniques with templates and tutorials'
];

const professionalSummaryFeatures = [
  'Configure workspace data connections',
  'Unlimited projects',
  'Unlimited Magic actions',
  'Save personal components (reusable groups of cells)',
  'Restore versions up to 30 days',
  'Automate scheduled runs',
  <>
    <ComputeModalLink>Medium</ComputeModalLink> compute included
  </>
];

const teamSummaryFeatures = [
  <>
    Dig into data apps with{' '}
    <LinkWithQuery isExternal={true} to={DOCS_LINKS.Explore}>
      visual exploration
    </LinkWithQuery>{' '}
    and{' '}
    <LinkWithQuery isExternal={true} to={DOCS_LINKS.ViewData}>
      view data
    </LinkWithQuery>
  </>,
  <>
    Add on{' '}
    <LinkWithQuery isExternal={true} to={DOCS_LINKS.Roles}>
      Explorer seats
    </LinkWithQuery>{' '}
    for business analysts and stakeholders
  </>,
  <>
    Approve changes with{' '}
    <LinkWithQuery isExternal={true} to={DOCS_LINKS.Reviews}>
      Reviews
    </LinkWithQuery>
  </>,
  'Define user groups to manage permissions',
  'Unlimited collections',
  'Share components (reusable groups of cells)',
  'Integrate with GitHub, GitLab, Airflow, Dagster & dbt',
  'Restore projects from unlimited version history',
  'Customize project organization and branding',
  'Automate unlimited scheduled runs w/ screenshot notifications',
  <>
    <ComputeModalLink>Advanced compute</ComputeModalLink> add-ons
  </>
];

const enterpriseSummaryFeatures = [
  'Integrate with Okta or any OIDC SSO provider',
  'Single tenant, EU multi-tenant, and HIPAA multi-tenant',
  <>
    Purchase through{' '}
    <LinkWithQuery to="https://aws.amazon.com/marketplace/pp/prodview-fbx3xfj7pdvog?sr=0-1&ref_=beagle&applicationId=AWSMPContessa">
      AWS Marketplace
    </LinkWithQuery>
  </>,
  <>
    Purchase through{' '}
    <LinkWithQuery to="https://app.snowflake.com/marketplace/listing/GZTYZASX5DF/hex-hex-marketplace-offering?search=hex">
      Snowflake Marketplace
    </LinkWithQuery>
  </>,
  <LinkWithQuery to="https://learn.hex.tech/docs/administration/workspace_settings/custom-images">
    Custom image add-ons
  </LinkWithQuery>,
  <LinkWithQuery to={INTERNAL_LINKS.EmbeddedAnalytics}>
    Embedded analytics add-on
  </LinkWithQuery>,
  'Build, troubleshoot and plan with dedicated support'
];

const USAGE_SECTION: SectionType = {
  //title: 'Usage',
  rows: [
    {
      label: 'Unlimited users',
      Community: [{ tick: true }],
      Professional: [{ tick: true }],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Projects',
      Community: [
        {
          text: 'Up to 5'
        }
      ],
      Professional: [
        {
          tick: true
        }
      ],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Explorer role',
      tooltip:
        'Explorers build explorations from data apps and data tables, and can set up conditional notifications. ',
      tooltipCTA: DOCS_LINKS.Roles,
      Community: [],
      Professional: [],
      Team: [
        {
          text: 'Per seat add-on'
        }
      ],
      Enterprise: [
        {
          text: 'Per seat add-on'
        }
      ]
    }
  ]
};

const DATA_SECTION: SectionType = {
  title: 'Data',
  rows: [
    {
      label: 'Project connections',
      Community: [
        {
          tick: true
        }
      ],
      Professional: [
        {
          tick: true
        }
      ],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Workspace connections',
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: true
        }
      ],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Data permissions',
      tooltip:
        'Manage and restrict access to projects that use sensitive data.',
      tooltipCTA: DOCS_LINKS.DataConnectionPermissions,
      Community: [],
      Professional: [
        {
          text: 'Restrict editing'
        }
      ],
      Team: [
        {
          text: 'Restrict editing'
        }
      ],
      Enterprise: [
        {
          text: 'Restrict editing & viewing'
        }
      ]
    },
    {
      label: 'Magic',
      tooltip:
        'A powerful set of AI-driven features to help you do more with data.',
      tooltipCTA: DOCS_LINKS.Magic,
      Community: [
        {
          text: 'Up to 100 actions per Editor/mo'
        }
      ],
      Professional: [
        {
          tick: true
        }
      ],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Custom SQL formatting',
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: true
        }
      ],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Explore & View data',
      tooltip:
        'Drill down into the underlying data powering a chart or table in a published App.',
      tooltipCTA: DOCS_LINKS.Explore,
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: false
        }
      ],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'SSH',
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: true
        }
      ],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Compute profiles',
      Community: [
        {
          text: <ComputeModalLink>Small</ComputeModalLink>
        }
      ],
      Professional: [
        {
          text: <ComputeModalLink>Medium</ComputeModalLink>
        }
      ],
      Team: [
        {
          text: (
            <>
              <ComputeModalLink>Advanced compute</ComputeModalLink> add-ons
            </>
          )
        }
      ],

      Enterprise: [
        {
          text: (
            <>
              <ComputeModalLink>Advanced compute</ComputeModalLink> add-ons
            </>
          )
        }
      ]
    },
    // {
    //   label: 'Custom images',
    //   Community: [],
    //   Professional: [
    //     {
    //       text: '1 pre-built custom image'
    //     }
    //   ],
    //   Team: [
    //     {
    //       text: 'X custom images, with permissions and compute profile pinning'
    //     }
    //   ],

    //   Enterprise: [
    //     {
    //       text: 'X custom images, with permissions and compute profile pinning'
    //     }
    //   ]
    // },
    {
      label: 'Scheduled runs',
      Community: [],
      Professional: [
        {
          text: '1 per project'
        }
      ],
      Team: [
        {
          text: 'Unlimited, plus email & Slack notifications, automated screenshots & custom cron schedules'
        }
      ],

      Enterprise: [
        {
          text: 'Unlimited, plus email & Slack notifications, automated screenshots, & custom cron schedules'
        }
      ]
    }
  ]
};

const COLLABORATION_SECTION: SectionType = {
  title: 'Collaboration',
  rows: [
    {
      label: 'Collections',
      tooltip:
        'Groups of projects that can be created, populated, and shared by users in a workspace',
      tooltipCTA: DOCS_LINKS.Collections,
      Community: [],
      Professional: [
        {
          text: 'Up to 3 shared collections'
        }
      ],
      Team: [
        {
          text: 'Unlimited'
        }
      ],
      Enterprise: [
        {
          text: 'Unlimited'
        }
      ]
    },
    {
      label: 'Components',
      tooltip:
        'Create reusable groups of cells that can be shared with your workspace. All paid plans include unlimited personal components.',
      tooltipCTA: DOCS_LINKS.Components,
      Community: [],
      Professional: [
        {
          text: 'Up to 3 shared components'
        }
      ],
      Team: [
        {
          text: 'Unlimited'
        }
      ],
      Enterprise: [
        {
          text: 'Unlimited'
        }
      ]
    },
    {
      label: 'Auto-archive',
      tooltip:
        'Configure rules to automatically archive projects that are no longer used',
      Community: [],
      Professional: [],
      Team: [
        {
          tick: true
        }
      ],

      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Version history',
      Community: [
        {
          text: '7-day'
        }
      ],
      Professional: [
        {
          text: '30-day'
        }
      ],
      Team: [
        {
          text: 'Unlimited'
        }
      ],

      Enterprise: [
        {
          text: 'Unlimited'
        }
      ]
    },
    {
      label: 'Reviews',
      tooltip: 'Protect business-critical projects with required reviews',
      tooltipCTA: DOCS_LINKS.Reviews,
      Community: [],
      Professional: [],
      Team: [
        {
          tick: true
        }
      ],

      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Custom labels',
      tooltip: 'Organize your app library for easy discoverability.',
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: false
        }
      ],
      Team: [
        {
          tick: true
        }
      ],

      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Custom branding',
      tooltip:
        'Style published apps with custom logo, chart colors, background color, and accent color.',
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: false
        }
      ],
      Team: [
        {
          text: 'Up to 1 custom app theme'
        }
      ],

      Enterprise: [
        {
          text: 'Up to 5 custom app themes '
        }
      ]
    },
    {
      label: 'Snapshots',
      tooltip: 'Freeze an app in time for easy reference in the future.',
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: true
        }
      ],
      Team: [
        {
          tick: true
        }
      ],

      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Presentation mode',
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: true
        }
      ],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'App speed configuration',
      Community: [],
      Professional: [
        {
          text: 'Query caching'
        }
      ],
      Team: [
        {
          text: 'Query caching and first-run acceleration'
        }
      ],

      Enterprise: [
        {
          text: 'Query caching and first-run acceleration'
        }
      ]
    },
    {
      label: 'Git project export and package import',
      Community: [],
      Professional: [],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    }
  ]
};

const ADMINISTRATIONS_SECTION: SectionType = {
  title: 'Administration',
  rows: [
    {
      label: 'User groups',
      tooltip: 'Create user groups to manage permissions',
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: false
        }
      ],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Usage reports',
      tooltip:
        'Admins can view usage of data connections, secrets and projects',
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: false
        }
      ],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Integrations',
      tooltip:
        'Integrations with your favorite orchestration and transformation tools',
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: false
        }
      ],
      Team: [
        {
          text: `Airflow, Dagster, dbt`
        }
      ],
      Enterprise: [
        {
          text: `Airflow, Dagster, dbt`
        }
      ]
    },
    {
      label: 'Public API',
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: false
        }
      ],
      Team: [
        {
          tick: true
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    }
  ]
};

const SECURITY_SECTION: SectionType = {
  title: 'Security',
  rows: [
    {
      label: 'OIDC SSO',
      Community: [
        {
          tick: false
        }
      ],
      Professional: [
        {
          tick: false
        }
      ],
      Team: [
        {
          tick: false
        }
      ],
      Enterprise: [
        {
          tick: true
        }
      ]
    },
    {
      label: 'Deployment',
      Community: [
        {
          text: 'Multi-tenant'
        }
      ],
      Professional: [
        {
          text: 'Multi-tenant'
        }
      ],
      Team: [
        {
          text: 'EU Multi-tenant (contract only) or multi-tenant'
        }
      ],
      Enterprise: [
        {
          text: 'Single, EU Multi-tenant or multi-tenant'
        }
      ]
    }
  ]
};

const SUPPORT_SECTION: SectionType = {
  title: 'Support',
  rows: [
    {
      label: 'Support',
      Community: [
        {
          text: 'Email'
        }
      ],
      Professional: [
        {
          text: 'Email'
        }
      ],
      Team: [
        {
          text: 'Email with priority SLA'
        }
      ],
      Enterprise: [
        {
          text: 'Dedicated email & option for live chat support, priority SLA, activation & ongoing live training'
        }
      ]
    },
    {
      label: 'Marketplace Offerings',
      Community: [],
      Professional: [],
      Team: [],
      Enterprise: [
        {
          text: 'AWS Marketplace'
        },
        {
          text: 'Snowflake Marketplace'
        }
      ]
    }
  ]
};

export const HEX_PLANS: PlanType = {
  Info: {
    Community: {
      title: 'Community',
      description: 'Free to explore data and showcase your work.',
      ctaText: 'Get started',
      ctaLink: `${SIGNUP_LINK}?plan=community&source=pricing`,
      price: 'Free',
      showInGrid: true,
      summaryInfo: communitySummaryFeatures
    },
    Professional: {
      title: 'Professional',
      description:
        'For practitioners or small teams accelerating data workflows.',
      ctaText: 'Get started',
      ctaLink: `${SIGNUP_LINK}?plan=professional&source=pricing`,
      priceDetail: ' per Editor / month',
      price: '$36',
      showInGrid: true,
      summaryInfo: professionalSummaryFeatures
    },
    Team: {
      buttonStyle: 'loud',
      title: 'Team',
      description: 'For teams scaling data collaboration and governance.',
      ctaText: 'Try 14 days free',
      ctaLink: `${SIGNUP_LINK}?plan=team&source=pricing`,
      price: '$75',
      priceDetail: ' per Editor / month',
      showInGrid: true,
      summaryInfo: teamSummaryFeatures
    },
    Enterprise: {
      title: 'Enterprise',
      description: 'Advanced security, support and control for organizations.',
      ctaText: 'Contact us',
      ctaLink: INTERNAL_LINKS.ContactUs,
      price: 'Custom',
      showInGrid: true,
      summaryInfo: enterpriseSummaryFeatures,
      trackingLabel: TRACKING_LABELS.ContactSales
    }
  },
  Sections: [
    USAGE_SECTION,
    DATA_SECTION,
    COLLABORATION_SECTION,
    ADMINISTRATIONS_SECTION,
    SECURITY_SECTION,
    SUPPORT_SECTION
  ]
};

export interface PlanType {
  Info: {
    Community: PlanInfoType;
    Enterprise: PlanInfoType;
    Professional: PlanInfoType;
    Team: PlanInfoType;
  };
  Sections: SectionType[];
}

export interface PlanInfoType {
  buttonStyle?: Stylez;
  ctaLink: string;
  ctaText: string;
  description: string;
  price: string;
  priceDetail?: string;
  showInGrid: boolean;
  summaryInfo?: ReactNode[];
  title: string;
  trackingLabel?: string;
}

export interface SectionType {
  rows: RowType[];
  title?: string;
}

interface RowType {
  Community: OptionType[];
  Enterprise: OptionType[];
  Professional: OptionType[];
  Team: OptionType[];
  label?: string | ReactNode;
  tooltip?: string;
  tooltipCTA?: string;
}

export interface OptionType {
  text?: string | ReactNode;
  tick?: boolean;
}

export const getPrevPlan = (which: string) => {
  switch (which) {
    case 'Community':
      return '';
    case 'Professional':
      return 'Community';
    case 'Team':
      return 'Professional';
    case 'Enterprise':
    default:
      return 'Team';
  }
};
